import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs } from '../../components';
import { useBranchShowrooms } from '../../hooks/use-branch-showrooms';
import LatestArticle from '../../components/template/category-advice/article';
import ThreeGridImage from '../../components/template/category-advice/image-grid';
import AnchorLinks from '../../components/common/anchor-links';
import DynamicComponent from '../../components/common/dynamic-component';
import CardSection from '../../components/common/card-section';
import NearbyShowrooms from '../../components/template/branch-showroom/nearby-showrooms';
import LocalNavTop from '../../components/common/local-nav-top';
import HeroSimple from '../../components/common/hero-simple';
import styles from './index.module.scss';
import { isValidArray } from '../../components/utilities/validations';

const CategoryTipsAdvice = (props) => {
	const pageData = props.data.contentstackCategoryAdvice;
	const configData = props.data.contentstackCategoryTipsAdviceConfig;
	const combinedShowrooms = [...props.data.showroomsByBranch?.nodes, ...props.data.showroomsByStoreNumber?.nodes];
	const { showrooms, error } = useBranchShowrooms(pageData.branch_data?.[0]?.branch_retail, combinedShowrooms, pageData.branch_data?.[0]?.additional_showrooms);

	return (
		<Layout id='template-CategoryTipsAdvice'>
			{isValidArray(pageData?.branch_data?.[0]?.local_nav) && (
				<LocalNavTop
					branchData={pageData.branch_data[0]}
					linksArray={pageData?.branch_data?.[0].local_nav}
					isAnchors={false}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{isValidArray(pageData.page_hero) && <HeroSimple data={pageData.page_hero[0]} />}

			<Container width={1800}>
				{pageData.category_type === 'article' && pageData.top_content.heading && <h1 className={`${styles.topContent_Heading} s1-h1___alt`}>{pageData.top_content.heading}</h1>}

				{pageData.top_content.subheading && (
					<div
						className={`${pageData.category_type === 'article' ? styles.topContent_Subheading : styles.localProject_Subheading}`}
						dangerouslySetInnerHTML={{
							__html: pageData.top_content.subheading,
						}}
					/>
				)}

				{pageData.top_content.ctas?.[0] && <AnchorLinks data={pageData.top_content.ctas} />}
			</Container>

			{(pageData.category_type === 'local projects' && (
				<Container
					width={1800}
					className={styles.localProject_threeGridImage}
				>
					<ThreeGridImage
						data={pageData.featured}
						heading={pageData.featured_article}
					/>
				</Container>
			)) || (
				<ThreeGridImage
					data={pageData.featured}
					heading={pageData.featured_article}
				/>
			)}

			{pageData.filter_tags && (
				<div className={pageData.category_type === 'local projects' ? styles.latestLocalProject : styles.latestArticle}>
					<LatestArticle
						articleTag={pageData.filter_tags}
						heading={pageData.latest_article}
						categoryType={pageData.category_type}
						config_data={configData}
						domain={props.location.origin}
					/>
				</div>
			)}

			{isValidArray(pageData.all_dynamic) &&
				pageData.all_dynamic.map((item, index) => (
					<DynamicComponent
						key={index}
						data={item}
					/>
				))}

			{isValidArray(pageData.cards) &&
				pageData.cards.map((card, index) => (
					<CardSection
						data={card}
						key={index}
					/>
				))}

			{pageData.nearby_showrooms?.branch_id && (
				<div className='my-8 md:my-12'>
					<Container>
						{pageData.nearby_showrooms.heading && <h2>{pageData.nearby_showrooms.heading}</h2>}
						<NearbyShowrooms
							locations={showrooms.filter((showroom) => (pageData.branch_data?.[0]?.by_state ? showroom.state === pageData.branch_data?.[0]?.state?.abbreviation : true))}
							error={error}
						/>
					</Container>
				</div>
			)}
		</Layout>
	);
};

CategoryTipsAdvice.propTypes = {
	data: PropTypes.object,
	className: PropTypes.string,
	location: PropTypes.object,
};

export default CategoryTipsAdvice;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => (
	<SEO
		// eslint-disable-next-line react/prop-types -- fix this
		{...data.contentstackCategoryAdvice.seo}
	/>
);

export const query = graphql`
	query ($uid: String!, $branch_id: [String], $store_numbers: [String]) {
		contentstackCategoryTipsAdviceConfig {
			show_more_button
		}

		contentstackCategoryAdvice(uid: { eq: $uid }) {
			url
			category_type
			branch_data {
				...BranchData
			}
			page_hero {
				...NewHero
			}
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			filter_tags
			top_content {
				heading
				subheading
				ctas {
					...CTA
				}
			}
			featured {
				...Article
			}
			featured_article
			latest_article
			all_dynamic {
				heading
				dynamic {
					...Dynamic
				}
			}
			cards {
				...CardSection
			}
			nearby_showrooms {
				heading
				branch_id
			}
			seo {
				...SEO
			}
		}

		showroomsByBranch: allShowrooms(filter: { branch_id: { in: $branch_id } }) {
			nodes {
				...Showroom
			}
		}

		showroomsByStoreNumber: allShowrooms(filter: { store_number: { in: $store_numbers } }) {
			nodes {
				...Showroom
			}
		}
	}
`;
